import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PageHeader from "../components/PageHeader";
import { Mail, ShieldCheck, Globe, FileSignature, CheckCircle2, Circle } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";

export default function IntegrationsPage() {
    const isSuperAdmin = useAuth((s) => s.user?.role_global === "super_admin");
    const [wr, setWr] = useState(null);

    useEffect(() => {
        if (!isSuperAdmin) return;
        api.get("/admin/integrations/webrisk")
            .then((r) => setWr(r.data))
            .catch(() => setWr(null));
    }, [isSuperAdmin]);

    const wrActive = !!(wr?.is_enabled && wr?.key_configured);

    const cards = [
        {
            key: "mailbox", to: "/mailboxes", icon: Mail,
            title: "Caselle PEC",
            desc: "Sincronizzazione automatica IMAP read-only",
            active: true, badges: [{ label: "Attivo", tone: "green" }],
        },
        {
            key: "av", icon: ShieldCheck,
            title: "Antivirus",
            desc: "Analisi euristica locale attiva (macro, doppie estensioni, PDF con JS, MIME mismatch, archivi cifrati).",
            active: true,
            badges: [
                { label: "Attivo (locale)", tone: "green" },
                { label: "Esterno non configurato", tone: "grey" },
            ],
            hint: "Adapter pronto per antivirus commerciali (ClamAV, VirusTotal) — opzionale.",
        },
        {
            key: "dom", to: isSuperAdmin ? "/integrations/webrisk" : undefined, icon: Globe,
            title: "Reputazione dominio",
            desc: "Analisi euristica locale (Punycode, shorteners, IP literal, homoglyph) + verifica esterna Google Web Risk (opzionale).",
            active: true,
            badges: [
                { label: "Attivo (locale)", tone: "green" },
                wrActive
                    ? { label: "Google Web Risk", tone: "blue" }
                    : { label: "Esterno non configurato", tone: "grey" },
            ],
            hint: isSuperAdmin
                ? "Clicca per configurare la chiave Google Web Risk."
                : undefined,
        },
        {
            key: "sig", icon: FileSignature,
            title: "Firma esterna",
            desc: "Trust List AgID attiva (54 provider ufficiali, 212 certificati). Verifica S/MIME + chain walk implementati localmente.",
            active: true,
            badges: [
                { label: "Attivo (locale)", tone: "green" },
                { label: "Esterno non configurato", tone: "grey" },
            ],
            hint: "Adapter pronto per motori di verifica firma avanzati (Aruba PEC Sign, InfoCert Sign) — opzionale.",
        },
    ];

    return (
        <div className="space-y-4">
            <PageHeader title="Integrazioni" subtitle="Connessione con caselle PEC e sistemi esterni"
                breadcrumbs={[{ label: "Integrazioni" }]} />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="integrations-grid">
                {cards.map((c) => {
                    const Icon = c.icon;
                    const inner = (
                        <div
                            className={`bg-white border border-[#DCE3EE] rounded-xl p-5 h-full flex flex-col ${c.to ? "hover:shadow-md transition-shadow cursor-pointer" : ""}`}
                            data-testid={`integration-card-${c.key}`}
                        >
                            <div className="flex items-center gap-3 mb-3">
                                <div className="w-10 h-10 rounded-lg bg-[#0B1633] text-white flex items-center justify-center">
                                    <Icon className="w-5 h-5" />
                                </div>
                                <div className="text-sm font-semibold text-[#172033]">{c.title}</div>
                            </div>
                            <div className="text-xs text-[#64748B] leading-relaxed">{c.desc}</div>
                            <div className="mt-3 flex flex-wrap items-center gap-1.5">
                                {c.badges.map((b, i) => (
                                    <span
                                        key={i}
                                        className={`text-[10px] font-medium px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${
                                            b.tone === "green" ? "bg-[#DCFCE7] text-[#166534]" :
                                            b.tone === "blue" ? "bg-[#DBEAFE] text-[#1E40AF]" :
                                            "bg-[#F1F5F9] text-[#64748B]"
                                        }`}
                                    >
                                        {b.tone === "green"
                                            ? <CheckCircle2 className="w-3 h-3" />
                                            : <Circle className="w-3 h-3" />}
                                        {b.label}
                                    </span>
                                ))}
                            </div>
                            {c.hint && (
                                <div className="mt-3 text-[10px] text-[#94A3B8] italic">{c.hint}</div>
                            )}
                        </div>
                    );
                    return c.to
                        ? <Link key={c.key} to={c.to}>{inner}</Link>
                        : <div key={c.key}>{inner}</div>;
                })}
            </div>
        </div>
    );
}
